import { useState } from "react";

import type { MediaRef } from "@motolynk/api-client";
import { useTranslation } from "@motolynk/i18n";
import { Button, Icon, MediaThumb } from "@motolynk/ui";

import { imageUrl } from "./format";
import { MediaPickerDialog } from "./MediaPickerDialog";

/**
 * One image chosen from the media library for a form (category image, brand or courier logo): preview, choose /
 * change, remove. Only public platform images are offered (the storefront shows them).
 */
export function ImageField({
  value,
  onChange,
  label,
  disabled,
}: {
  value: MediaRef | null;
  onChange: (value: MediaRef | null) => void;
  label: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("medialib");
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <MediaThumb
        item={{ kind: "image", thumbnailUrl: value?.url ?? null, title: label }}
        className="size-16 shrink-0 rounded-md border border-border"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-label={`${value ? t("imageField.change") : t("imageField.choose")}: ${label}`}
        >
          <Icon name="image" size={14} />
          {value ? t("imageField.change") : t("imageField.choose")}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(null)}
            aria-label={`${t("imageField.remove")}: ${label}`}
          >
            {t("imageField.remove")}
          </Button>
        )}
      </div>
      <MediaPickerDialog
        open={open}
        onOpenChange={setOpen}
        kind="image"
        storefront
        initial={value ? [value.uid] : []}
        onPick={([media]) => {
          onChange(media ? { uid: media.uid, url: imageUrl(media, "small") } : null);
        }}
      />
    </div>
  );
}
